import { AFFILIATION_TYPE_LABELS as AFFILIATION_TYPE_LABELS_BY_CATEGORY } from '../../utils/affiliationTypes';
// Shared types and UI constants for the social graph page.

import type { LayoutMode } from './layoutPipeline';

export type AnalysisMode = 'exploratory' | 'zscore';

// Kill-switch (PM decision 2026-09-01): the backend z-score calculation is
// known to produce wrong values, so EVERY z-score surface is hidden until the
// fix ships. Keyed off this: the Analysis mode toggle (mode then stays
// 'exploratory' — the toggle is its only setter), the Anomaly Score size
// metric, the z-score report button, the Z-Score anomaly actor groups, and
// the Avg Z-Score / Anomaly score panel rows. Flip back to true to restore
// everything; note graphs generated while the bug was live keep wrong stored
// values until regenerated.
export const ZSCORE_ENABLED = false;
export type ScopeType = 'event' | 'narrative' | 'actor_group';
export type GraphType = 'mention' | 'repost';
export type ActiveGraphKind = 'mention' | 'case';
export type NodeSizeMetric = 'pagerank' | 'betweenness' | 'hub_score' | 'authority_score' | 'degree' | 'anomaly';

export const SIZE_METRIC_LABELS: Record<NodeSizeMetric, string> = {
  pagerank: 'PageRank',
  betweenness: 'Betweenness',
  hub_score: 'Hub Score',
  authority_score: 'Authority Score',
  degree: 'Degree',
  anomaly: 'Anomaly Score',
};

// Metrics offered in the Node size dropdown (PM decision 2026-09-16): only
// Degree (default) and PageRank until the others can be explained to users.
// Betweenness / hub / authority stay computed and typed — the union, labels
// and buildGraph sizing all still support them; only the picker is narrowed.
// 'anomaly' is appended by the panel while ZSCORE_ENABLED is on.
export const VISIBLE_SIZE_METRICS: NodeSizeMetric[] = ['degree', 'pagerank'];
export const DEFAULT_SIZE_METRIC: NodeSizeMetric = 'degree';

// Layouts offered in the Layout dropdown (PM decision 2026-09-17): Hub-centered
// (radial) is hidden until edges can be bundled like the NVIDIA reference. The
// pipeline, legend caption and LayoutMode union keep supporting it — only the
// picker is narrowed, so restoring it is adding 'radial' back here.
export const VISIBLE_LAYOUT_MODES: LayoutMode[] = ['organic', 'packed'];

// Actor-role categories hidden from the UI (PM decision 2026-09-17): no row in
// the Actors tab and no line in the Information panel's Categories card. The
// role still exists on nodes and in spotlights; delete the entry to bring it back.
export const HIDDEN_ACTOR_ROLE_CATEGORIES: string[] = ['Regular Node'];

// Option hints shown beside the open Node size metric dropdown (Figma 683-1325)
export const SIZE_METRIC_DESCRIPTIONS: Record<NodeSizeMetric, string> = {
  pagerank:
    'Influence weighted by who references the actor: a mention from a well-connected actor counts for more than a mention from a random one.',
  betweenness:
    'How often the actor sits on the shortest path between two others. Information crossing the network is likely to pass through it.',
  hub_score: 'An actor that references many strong sources. It curates and aggregates rather than originating.',
  authority_score: 'An actor that strong hubs reference. An original source others rely on.',
  degree: 'The plain count of actors this one interacts with, inbound and outbound. No weighting.',
  anomaly: 'Combined anomaly across the actor’s relations, weighted by interaction volume.',
};

// The "Thickness by interactions" switch (Display options) sizes every resting
// edge by interaction volume — the same scale the selected-actor state uses.
// PM rule: it starts ON for single-actor explore graphs (few edges, and the
// volume differences ARE the point) and OFF for everything else (dense graphs
// get visually heavy) — see the isExploreGraph-keyed effect in SocialGraphPOC.
// While volume sizing is on, the reciprocal +0.3 bump doesn't apply: each
// direction is sized by its own volume.

// Log scale so heavy-tail pairs don't become ribbons, clamped so dense fans
// stay readable. Tune the three constants against real data with the designer.
// Retuned 2026-09-04: the first pass (scale 0.55, max 4) hit the cap at ~55
// interactions, so a 500-interaction edge and an 8000-interaction edge rendered
// identically. The flatter slope + higher cap keep the curve rising until
// ~19k interactions (1 → 1.2, 10 → 2.2, 100 → 3.5, 500 → 4.4, 8000 → 6.0).
const EDGE_VOLUME_MIN_SIZE = 0.8;
const EDGE_VOLUME_SIZE_SCALE = 0.4;
const EDGE_VOLUME_MAX_SIZE = 6.5;

export function edgeVolumeSize(totalCount: number): number {
  return Math.min(EDGE_VOLUME_MIN_SIZE + Math.log2(totalCount + 1) * EDGE_VOLUME_SIZE_SCALE, EDGE_VOLUME_MAX_SIZE);
}

// The switch-off formula: subtle weight-based thickness with a small bump for
// reciprocal pairs (the original resting look)
export function classicEdgeSize(weight: number, reciprocal: boolean): number {
  const base = Math.min(0.35 + Math.log2(weight + 1) * 0.25, 1.2);
  return reciprocal ? Math.min(base + 0.3, 1.6) : base;
}

// Starting configuration for single-actor explore graphs (result.explore set).
// One ego network reads differently from a multi-community graph, so it opens
// with its own defaults: plain Degree sizing (direct connections around the
// actor), platform colors instead of PageRank + community, and the
// Hub-centered (radial) layout — the explored actor IS the hub, so radial
// distance reads as closeness to it (PM rule 2026-09-02). Radial was hidden
// from the picker on 2026-09-17 (see VISIBLE_LAYOUT_MODES), so explore graphs
// open in Organic bridges until it returns; then switch this back to 'radial'.
export const EXPLORE_GRAPH_DEFAULTS: {
  sizeMetric: NodeSizeMetric;
  colorMode: GraphColorMode;
  layoutMode: LayoutMode;
} = {
  sizeMetric: 'degree',
  colorMode: 'platform',
  layoutMode: 'organic',
};

// Option hints for the Layout dropdown
export const LAYOUT_MODE_DESCRIPTIONS: Record<LayoutMode, string> = {
  organic:
    'Clusters settle around each other and actors connecting several communities drift into the gaps between them. Best for spotting bridges.',
  packed: 'Each community keeps its own tight circle with clear space around it. Best for comparing communities at a glance.',
  radial:
    'The top actor by the size metric sits at the center of each community; the rest ring around it, farther out the lower they rank.',
};

// Explanation shown under the Analysis mode switcher for the active mode
export const ANALYSIS_MODE_DESCRIPTIONS: Record<AnalysisMode, string> = {
  exploratory:
    'Maps the structure of the network: who interacts with whom, which communities form, and who holds the central positions. Edges are coloured by community.',
  zscore:
    'Measures how far each interaction’s intensity sits from the network average, and colours edges by that deviation. Adds behavioural metrics to the actor panel.',
};

export interface NodeMetricValues {
  pagerank: number;
  betweenness: number;
  hubScore: number;
  authorityScore: number;
  degree: number;
  anomaly: number;
}

// Node-level z-score anomaly buckets — same thresholds as the edge scale (FDE spec),
// applied to a node's avg z-score. Ordered most-severe first for panel listings.
export type ZScoreBucket = 'high' | 'elevated' | 'normal' | 'low';
export const ZSCORE_BUCKETS: ZScoreBucket[] = ['high', 'elevated', 'normal', 'low'];
export const ZSCORE_BUCKET_LABELS: Record<ZScoreBucket, string> = {
  high: 'Z > 2 — suspected coordination',
  elevated: '1 < Z ≤ 2 — needs review',
  normal: '0 ≤ Z ≤ 1 — within norm',
  low: 'Z < 0 — low activity',
};

// Boolean node categories surfaced as clickable spotlight groups in the Actors tab.
// Unlike roles these can overlap — one actor can be both high-risk and a bridge.
export type NodeFlag = 'highRisk' | 'stateAffiliated' | 'connector' | 'highInteraction';
export const NODE_FLAG_LABELS: Record<NodeFlag, string> = {
  highRisk: 'High Risk Actors',
  stateAffiliated: 'State Affiliated',
  connector: 'Critical bridges',
  highInteraction: 'High-Interaction Actors',
};

// One-line explanations behind the ⓘ hint on Actors-tab rows. Keys mix both
// role systems: mention-graph roles plus the case-graph Producer/Mixed pair
// ('Amplifier' exists in both systems and shares one blurb that fits either).
// Rows whose role has no entry simply render without a hint.
export const ACTOR_ROLE_DESCRIPTIONS: Record<string, string> = {
  'Inauthentic Coordination': 'Accounts whose synchronized activity suggests coordinated or automated behavior.',
  'Bridge': 'Connects otherwise separate communities and carries information between them.',
  'Broadcaster': 'A source many actors reference — its content spreads outward.',
  'Amplifier': 'Actively spreads other actors’ content, boosting its reach.',
  'Core Member': 'Deeply embedded in one community and central to its conversations.',
  'Active Participant': 'Interacts frequently across the graph without one dominant pattern.',
  'Regular Node': 'An ordinary actor with no distinctive structural pattern.',
  'Producer': 'Actors that only get reposted and never repost anyone else — they originate content.',
  'Mixed': 'Actors that both repost others and get reposted in turn.',
};

export const ZSCORE_BUCKET_DESCRIPTIONS: Record<ZScoreBucket, string> = {
  high: 'Interaction intensity far above the baseline — possible coordinated campaign or automation.',
  elevated: 'Noticeably above the baseline — worth an analyst’s review.',
  normal: 'Interaction intensity within the statistical norm.',
  low: 'Below the baseline — sporadic or fading activity.',
};

export const NODE_FLAG_DESCRIPTIONS: Record<NodeFlag, string> = {
  highRisk: 'Actors marked as high risk in the system.',
  stateAffiliated: 'Actors affiliated with state institutions or state media.',
  connector: 'Critical bridges are actors that connect to multiple communities',
  highInteraction: 'Actors with a significantly higher number of interactions than other actors in this graph.',
};

// Panel spotlight: dim everything except one community, one actor role, one
// z-score anomaly bucket, or one flag category
export type GraphHighlight =
  | { type: 'community'; value: number }
  | { type: 'role'; value: string }
  | { type: 'zscore'; value: ZScoreBucket }
  | { type: 'flag'; value: NodeFlag };

export const PANEL_TABS = ['configuration', 'filters', 'actors', 'search'] as const;
export type PanelTab = (typeof PANEL_TABS)[number];
export const PANEL_TAB_LABELS: Record<PanelTab, string> = {
  configuration: 'Configuration',
  filters: 'Filters',
  actors: 'Actors',
  search: 'Search',
};
// Actors listed in a drill-down group before the "+N more actors" cutoff
export const DRILL_MEMBERS_SHOWN = 100;

export interface NodeAttributes {
  label: string;
  x: number;
  y: number;
  size: number;
  color: string;
  inEvent: boolean;
  community: number;
  betweenness: number;
  pagerank: number;
  hubScore: number;
  authorityScore: number;
  actorRole: string;
  platform: string;
  // the platform scheme hue — rank-based per graph (see PLATFORM_PALETTE),
  // assigned once at build time like the edges' platformBase
  platformBase: string;
  sourceUrl: string | null;
  isKeyConnector: boolean;
  isHighRisk: boolean;
  isStateAffiliated: boolean;
  // total interaction volume across every connection of the actor (sum of the
  // incident edges' totalCount, both directions) — the "High-Interaction
  // Actors" category ranks and thresholds on it (see findHighInteractionActors)
  interactions: number;
  isHighInteraction: boolean;
  // categorical metadata driving the Filters tab's node facets; empty arrays when
  // the backend didn't return the fields (graphs generated before backend PR #823)
  discreditations: string[];
  affiliations: NodeAffiliation[];
  affiliationTypes: string[];
  affiliationCountries: string[];
  inauthenticTypes: string[];
  // public actor tags (mention graphs only; localized names come with the data)
  tags: NodeTagInfo[];
  // backend structural degree — drives the min-connections filter
  degree: number;
  // case-graph profile fields; null on mention graphs (their DTO doesn't carry them)
  isVerified: boolean | null;
  originCountry: string | null;
  audience: number | null;
  audienceBucket: string | null;
  isDust: boolean;
  // set by the node-filter effect; sigma skips hidden nodes and their edges
  hidden?: boolean;
  // set by the node-filter effect when "Highlight instead of hide" is on: the
  // node fails the facet filters but stays on the canvas as gray context. The
  // hover/spotlight reducers read it so they never repaint such a node vivid
  filterDimmed?: boolean;
  // behavioral / z-score metrics — null when the backend didn't compute them
  // (case graphs, or fields specific to z-score analysis)
  reciprocity: number | null;
  clusteringCoeff: number | null;
  platformDiversity: number | null;
  bridgeScore: number | null;
  weightGini: number | null;
  avgZScore: number | null;
  weightedAnomalyScore: number | null;
  // avg z-score bucketed at build time so the panel/spotlight don't re-derive it;
  // null when the backend didn't compute z-scores (case graphs, exploratory mode)
  zscoreBucket: ZScoreBucket | null;
  forceLabel?: boolean;
}

// Direction of an edge relative to the analysis scope: internal (in-scope →
// in-scope), inbound (external → in-scope), outbound (in-scope → external),
// external (external → external)
export type ScopeFlow = 'internal' | 'inbound' | 'outbound' | 'external';

export interface EdgeAttributes {
  dominantMentionType: string;
  mentionTypes: Record<string, number>;
  weight: number;
  totalCount: number;
  zScore: number;
  color: string;
  mentionBase: string;
  communityBase: string;
  platformBase: string;
  size: number;
  type: string;
  curvature: number;
  interCommunity: boolean;
  reciprocal: boolean;
  scopeFlow: ScopeFlow;
  hidden?: boolean;
  // true when an explicit edge filter removed this edge (interaction type,
  // mutual-only, anomaly bucket); feeds `hidden` and keeps the hover reducer
  // from re-revealing deliberately filtered edges
  typeFiltered?: boolean;
  // one of the TOP_VOLUME_EDGE_COUNT heaviest actor pairs by interactions (both
  // directions of a reciprocal pair flagged together) — set once at build time
  // by markTopVolumeEdges; only rendered distinctively while "Thickness by
  // interactions" is on
  topVolume?: boolean;
  // draw order; the volume-size effect lifts top-volume edges above the bundle
  zIndex?: number;
  // ranked below FAR_ZOOM_EDGE_LIMIT by interactions — skipped while zoomed out
  // (set once by markThinnedEdges; only on graphs above the limit)
  thinned?: boolean;
}

// one affiliation as stored on node attributes (DTO snake_case normalized)
export interface NodeAffiliation {
  entityType: string;
  country: string | null;
}

// one public tag as stored on node attributes (DTO snake_case normalized)
export interface NodeTagInfo {
  tag: string;
  nameEn: string;
  nameUk: string;
}

// ——— Node facet filters (Filters tab) ———
// Faceted narrowing over node metadata, computed entirely client-side from the
// cached response: values checked within a section OR-combine, active sections
// AND-combine, an empty section filters nothing. Non-matching nodes are hidden
// (positions kept — no relayout), their edges with them.
export type NodeFilterCategory =
  | 'scopes'
  | 'platforms'
  | 'communities'
  | 'roles'
  | 'zscoreBuckets'
  | 'tags'
  | 'affiliationTypes'
  | 'affiliationCountries'
  | 'discreditations'
  | 'inauthenticTypes'
  | 'originCountries'
  | 'audienceBuckets';

export const NODE_FILTER_CATEGORIES: NodeFilterCategory[] = [
  'scopes',
  'platforms',
  'communities',
  'roles',
  'zscoreBuckets',
  'tags',
  'affiliationTypes',
  'affiliationCountries',
  'discreditations',
  'inauthenticTypes',
  'originCountries',
  'audienceBuckets',
];

// categorical facets + the numeric node thresholds: min connections (0 = off),
// min avg Z-Score (null = off — 0 is a meaningful threshold)
export type NodeFilters = Record<NodeFilterCategory, string[]> & {
  minDegree: number;
  minAvgZScore: number | null;
};

export const EMPTY_NODE_FILTERS: NodeFilters = {
  scopes: [],
  platforms: [],
  communities: [],
  roles: [],
  zscoreBuckets: [],
  tags: [],
  affiliationTypes: [],
  affiliationCountries: [],
  discreditations: [],
  inauthenticTypes: [],
  originCountries: [],
  audienceBuckets: [],
  minDegree: 0,
  minAvgZScore: null,
};

export function hasActiveNodeFilters(filters: NodeFilters): boolean {
  return (
    filters.minDegree > 0 ||
    filters.minAvgZScore !== null ||
    NODE_FILTER_CATEGORIES.some((category) => filters[category].length > 0)
  );
}

// single-valued category: passes when inactive, or the node's value is checked
// (a null value never matches an active category — "no data" is excluded)
const matchesValue = (selected: string[], value: string | null): boolean =>
  selected.length === 0 || (value !== null && selected.includes(value));

// multi-valued category: any of the node's values checked
const matchesAny = (selected: string[], values: string[]): boolean =>
  selected.length === 0 || values.some((value) => selected.includes(value));

export function matchesNodeFilters(
  attrs: Pick<
    NodeAttributes,
    | 'inEvent'
    | 'platform'
    | 'community'
    | 'actorRole'
    | 'zscoreBucket'
    | 'tags'
    | 'affiliationTypes'
    | 'affiliationCountries'
    | 'discreditations'
    | 'inauthenticTypes'
    | 'originCountry'
    | 'audienceBucket'
    | 'degree'
    | 'avgZScore'
  >,
  filters: NodeFilters,
): boolean {
  if (filters.minDegree > 0 && attrs.degree < filters.minDegree) return false;
  if (
    filters.minAvgZScore !== null &&
    (attrs.avgZScore === null || attrs.avgZScore < filters.minAvgZScore)
  ) {
    return false;
  }
  return (
    matchesValue(filters.scopes, attrs.inEvent ? 'in_scope' : 'external') &&
    matchesValue(filters.platforms, attrs.platform) &&
    matchesValue(filters.communities, String(attrs.community)) &&
    matchesValue(filters.roles, attrs.actorRole) &&
    matchesValue(filters.zscoreBuckets, attrs.zscoreBucket) &&
    matchesAny(
      filters.tags,
      attrs.tags.map((tag) => tag.tag),
    ) &&
    matchesAny(filters.affiliationTypes, attrs.affiliationTypes) &&
    matchesAny(filters.affiliationCountries, attrs.affiliationCountries) &&
    matchesAny(filters.discreditations, attrs.discreditations) &&
    matchesAny(filters.inauthenticTypes, attrs.inauthenticTypes) &&
    matchesValue(filters.originCountries, attrs.originCountry) &&
    matchesValue(filters.audienceBuckets, attrs.audienceBucket)
  );
}

// actor-group membership facet values ↔ the i18n keys the node panel line and
// the legend already use for the same split
export const SCOPE_FILTER_LABELS: Record<string, string> = {
  in_scope: 'In actor group',
  external: 'Not in actor group',
};

// Audience-size buckets for the case-graph audience facet. Labels are numeric —
// no i18n needed.
export const AUDIENCE_BUCKET_ORDER = ['LT_1K', '1K_10K', '10K_100K', '100K_1M', 'GTE_1M'];
export const AUDIENCE_BUCKET_LABELS: Record<string, string> = {
  LT_1K: '< 1K',
  '1K_10K': '1K – 10K',
  '10K_100K': '10K – 100K',
  '100K_1M': '100K – 1M',
  GTE_1M: '≥ 1M',
};

export function audienceBucket(audience: number | null): string | null {
  if (audience === null || audience < 0) return null;
  if (audience < 1_000) return 'LT_1K';
  if (audience < 10_000) return '1K_10K';
  if (audience < 100_000) return '10K_100K';
  if (audience < 1_000_000) return '100K_1M';
  return 'GTE_1M';
}

// Discrete steps for the pruning sliders (min connections / min interactions):
// network metrics are heavy-tailed, so a linear slider wastes most of its track —
// these steps give a log-ish sweep. Slider position 0 = off.
export const FILTER_THRESHOLD_STEPS = [2, 3, 5, 10, 20, 50, 100, 250, 500, 1000];

export function filterThresholds(maxValue: number): number[] {
  return FILTER_THRESHOLD_STEPS.filter((step) => step <= maxValue);
}

// min avg Z-Score slider steps (0 is meaningful: it drops the below-baseline tail)
export const MIN_AVG_ZSCORE_STEPS = [0, 0.5, 1, 1.5, 2, 3];

// a facet value present in the graph + how many actors carry it; `label` is set
// when the display name comes from the data itself (tags) rather than a label map
export interface NodeFilterOption {
  value: string;
  count: number;
  label?: string;
}

// Wording matches the actor filters elsewhere in the app (utils/actors.jsx) —
// typed local maps because that file is untyped and keys its inauthentic map by
// the lowercase source-model flags, while the graph API returns these enums.
// buildGraph folds legacy backend values into the new categories, so only those need labels
export const AFFILIATION_TYPE_LABELS: Record<string, string> = { ...AFFILIATION_TYPE_LABELS_BY_CATEGORY };

export const DISCREDITATION_TYPE_LABELS: Record<string, string> = {
  SANCTIONS: 'Sanctions',
  DISINFORMATION: 'Information manipulation',
  HACKED_OR_STOLEN: 'Hackers / Hacktivists',
  INVOLVED_IN_INFLUENCE_OPERATIONS: 'Influence operations',
};

// backend buckets each account into at most one type, by this priority
export const INAUTHENTIC_TYPE_LABELS: Record<string, string> = {
  SUSPENDED: 'Suspended by platform',
  INAUTHENTIC_BEHAVIOR: 'Inauthentic behavior',
  IMMATURE_ACCOUNT: 'Immature accounts',
};
// While "Thickness by interactions" is on, the heaviest pairs get an extra
// treatment (brightest hue, full opacity, arrows, drawn on top) so an 8k-interaction
// relation is unmistakable next to a 500 one. Skipped on tiny graphs where the
// top 10 would be most of the edges anyway (single-actor explore graphs).
// Far-zoom thinning (team decision for the scaling plan: strongest-only edges
// when zoomed out). Graphs with more edges than the limit get every edge ranked
// below it flagged `thinned` at build time; the hover reducer skips those while
// the camera ratio is above FAR_ZOOM_CAMERA_RATIO (1 = the whole graph fits the
// viewport, smaller = zoomed in). Tune by measurement.
export const FAR_ZOOM_EDGE_LIMIT = 30_000;
export const FAR_ZOOM_CAMERA_RATIO = 0.6;

export const TOP_VOLUME_EDGE_COUNT = 10;
export const TOP_VOLUME_MIN_EDGES = 20;

// 'mono' paints the whole graph (nodes + edges) in one hue, Graphistry-style:
// with no categorical colors competing, edge density becomes a brightness map and
// structure carries the signal — useful when community detection is noisy.
// 'scope' colors nodes binary (in-scope vs external); edges only echo that split
// (internal traffic in the in-scope hue, anything touching an external actor gray).
// 'connection' paints only the edges that cross that boundary, by direction
// (external → in-scope cyan, in-scope → external orange — see SCOPE_EDGE_STYLES)
// while every node stays neutral gray. The two used to be one combined mode.
// 'platform' colors nodes by the actor's platform and edges by the source actor's
// platform — shows how the conversation spans platforms.
export type GraphColorMode = 'community' | 'mention' | 'mono' | 'scope' | 'connection' | 'platform';

export interface GraphStats {
  nodeCount: number;
  edgeCount: number;
  communityCount: number;
  externalCount: number;
  keyConnectorCount: number;
  totalActorsInScope: number;
  // scope actors missing from the graph, by reason (see SocialGraphGlobalMetrics)
  actorsWithoutInteractions: number;
  actorsOverLimit: number;
  density: number;
  reciprocity: number;
  producersCount?: number;
  amplifiersCount?: number;
}

export interface TooltipState {
  x: number;
  y: number;
  nodeId: string;
  label: string;
  inEvent: boolean;
  community: number;
  betweenness: number;
  pagerank: number;
  hubScore: number;
  authorityScore: number;
  actorRole: string;
  platform: string;
  sourceUrl: string | null;
  // Connections card: per-direction connection counts + interactions
  connections: ActorConnectionStats;
  mentionCounts: Record<string, number>;
  isKeyConnector: boolean;
  isHighRisk: boolean;
  isStateAffiliated: boolean;
  isHighInteraction: boolean;
  reciprocity: number | null;
  clusteringCoeff: number | null;
  platformDiversity: number | null;
  bridgeScore: number | null;
  weightGini: number | null;
  avgZScore: number | null;
  weightedAnomalyScore: number | null;
}

// Side panel data — TooltipState minus screen coordinates, plus the metadata the
// hover tooltip deliberately doesn't show (it stays minimal: name + platform)
export type SelectedNodeInfo = Omit<TooltipState, 'x' | 'y'> & {
  discreditations: string[];
  affiliations: NodeAffiliation[];
  inauthenticTypes: string[];
  tags: NodeTagInfo[];
  isVerified: boolean | null;
  originCountry: string | null;
  audience: number | null;
};

// One neighbor of the selected actor in the panel's Connections tab.
// direction is relative to the selected actor: 'in' = the neighbor mentions it,
// 'out' = it mentions the neighbor, 'mutual' = edges both ways.
export interface NodeConnection {
  id: string;
  label: string;
  platform: string;
  direction: 'in' | 'out' | 'mutual';
  // max z-score across the pair's edges; null outside z-score analysis
  zScore: number | null;
  anomalous: boolean;
  // total interaction volume with the neighbor — sort weight AND the
  // "N interactions" readout on the Connections tab rows
  strength: number;
  // per-direction split of strength (a mutual pair is two directed edges, each
  // with its own count); for one-directional rows one side is 0
  inboundCount: number;
  outboundCount: number;
}

export function matchesHighlight(
  attrs: Pick<
    NodeAttributes,
    | 'community'
    | 'actorRole'
    | 'zscoreBucket'
    | 'isHighRisk'
    | 'isStateAffiliated'
    | 'isKeyConnector'
    | 'isHighInteraction'
  >,
  highlight: GraphHighlight,
): boolean {
  if (highlight.type === 'community') return attrs.community === highlight.value;
  if (highlight.type === 'role') return attrs.actorRole === highlight.value;
  if (highlight.type === 'zscore') return attrs.zscoreBucket === highlight.value;
  if (highlight.value === 'highRisk') return attrs.isHighRisk;
  if (highlight.value === 'stateAffiliated') return attrs.isStateAffiliated;
  if (highlight.value === 'highInteraction') return attrs.isHighInteraction;
  return attrs.isKeyConnector;
}


export interface CommunityClusterItem {
  id: number;
  nodes: number;
  details: string[];
}

// Common shape of a group member across the Actors tab's group kinds — the
// drill-down list renders these with a platform icon
export interface ActorGroupMember {
  id: string;
  label: string;
  platform: string;
}

export interface RoleGroup {
  role: string;
  members: (ActorGroupMember & { size: number })[];
}

export interface ZScoreGroup {
  bucket: ZScoreBucket;
  members: (ActorGroupMember & { z: number })[];
}

// An actor's connections split the way the Connections tab classifies them —
// each neighbour is exactly one of inbound-only, outbound-only or mutual, so
// the three connection counts add up to the tab's total — with the
// interactions those connections carry (mutual = both directions summed).
// Computed once per graph (memberConnections in buildGraph.ts, keyed by actor
// id on the page) and shown under every member name in the group panel
export interface DirectionStat {
  connections: number;
  interactions: number;
}
export interface MemberConnections {
  inbound: DirectionStat;
  outbound: DirectionStat;
  mutual: DirectionStat;
}
// Same shape for the Actor panel's Connections card, but with the in/out
// counts including mutual pairs (see actorConnectionStats in buildGraph.ts)
export type ActorConnectionStats = MemberConnections;

// Members arrive pre-sorted by the page: by node size for most flags, by
// interaction volume (most first) for the High-Interaction category — the
// right-hand group panel keeps that order
export interface FlagGroup {
  flag: NodeFlag;
  members: (ActorGroupMember & { size: number; interactions: number })[];
}
